import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Tag, X } from "lucide-react";
import { api, fmt, formatApiError } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";

// Champ de saisie d'un code rabais (panier + caisse). Valide côté serveur,
// sans consommer d'utilisation (le compteur augmente seulement à la commande).
export function PromoCodeInput({ promo, onApply, onClear }) {
  const { t, ts } = useLang();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const apply = async () => {
    if (!code.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/promo/validate", { code: code.trim() });
      onApply(data);
      setCode("");
      toast.success(t("promo.applied", { code: data.code }));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("promo.invalid"));
    } finally {
      setLoading(false);
    }
  };

  if (promo) {
    const amount = promo.kind === "pct" ? `-${promo.value} %` : `-${fmt(promo.value)}`;
    return (
      <div data-testid="promo-applied" className="border border-gold/30 bg-gold/5 rounded-lg px-4 py-2.5">
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-champagne inline-flex items-center gap-2 min-w-0">
            <Tag className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{promo.code} · {amount} {t(`promo.scope_${promo.scope}`)}</span>
          </span>
          <button type="button" data-testid="promo-remove" onClick={onClear}
            className="text-ivory/65 hover:text-red-400 transition-colors duration-300 shrink-0" aria-label={ts("promo.remove")}>
            <X className="w-4 h-4" />
          </button>
        </div>
        {promo.auto && (
          <p className="text-xs text-gold/80 mt-1.5" data-testid="promo-auto-note">{t("promo.auto_short")}</p>
        )}
      </div>
    );
  }

  return (
    <div className="flex gap-2">
      <input
        data-testid="promo-code-input"
        type="text"
        value={code}
        onChange={(e) => setCode(e.target.value.toUpperCase())}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); apply(); } }}
        placeholder={ts("promo.placeholder")}
        maxLength={30}
        className="flex-1 min-w-0 bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm uppercase tracking-wider placeholder:normal-case placeholder:tracking-normal placeholder:text-ivory/60 focus:border-gold focus:outline-none"
      />
      <button type="button" data-testid="promo-apply" onClick={apply} disabled={loading || !code.trim()}
        className="px-5 py-2.5 rounded-full text-xs font-semibold border border-gold/40 text-gold hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-40 inline-flex items-center gap-2">
        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        {t("promo.apply")}
      </button>
    </div>
  );
}
